import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import OperationDialog from "@/components/OperationDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Undo2, Plus, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Returns() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const warehouses = useOptions("/warehouses");

  const process = async (row, action, reload) => {
    try { await api.post(`/returns/${row.id}/process`, { action }); toast.success(`Return processed (${action})`); reload(); }
    catch { toast.error("Failed"); }
  };

  const cols = [
    { key: "return_no", label: "Return No", mono: true },
    { key: "type", label: "Type", render: (r) => <span className="capitalize">{r.type}</span> },
    { key: "reason", label: "Reason" },
    { key: "reference", label: "Reference", mono: true },
    { key: "items", label: "Lines", render: (r) => (r.items || []).length },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const fields = [
    { name: "type", label: "Return Type", type: "select", required: true,
      options: ["customer", "vendor", "damaged"].map((v) => ({ value: v, label: v })) },
    { name: "reason", label: "Reason", type: "select", required: true,
      options: ["Damaged", "Wrong Item", "Expired", "Quality Issue", "Other"].map((v) => ({ value: v, label: v })) },
    { name: "reference", label: "Reference Doc" },
    { name: "warehouse_id", label: "Warehouse", type: "select", required: true, options: warehouses },
  ];

  return (
    <div>
      <PageHeader title="Returns" subtitle="Customer/vendor returns, inspection & restock" icon={Undo2}
        breadcrumb={["Home", "Operations", "Returns"]}
        actions={can("returns.create") && (
          <Button data-testid="add-return-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />New Return
          </Button>
        )} />
      <DataTable endpoint="/returns" columns={cols} testId="returns-table" refreshKey={refresh}
        actions={(row, reload) => row.status === "pending" && can("returns.approve") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`process-return-${row.id}`} className="text-blue-600 border-blue-200">
                <RotateCcw className="h-4 w-4 mr-1" />Process
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {["restock", "repair", "replace", "scrap"].map((a) => (
                <DropdownMenuItem key={a} onClick={() => process(row, a, reload)} className="capitalize">{a}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )} />
      <OperationDialog open={open} onOpenChange={setOpen} title="New Return" headerFields={fields}
        endpoint="/returns" onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
